import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import {
    TextButton,
    Button,
    Text,
} from '@zillow/constellation';

import { UserManagementSelectors } from 'app/shared/selectors';
import LoadingConstants from 'app/shared/constants/LoadingConstants';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import TableMFH from 'app/shared/components/TableMFH';
import UserManagementActions from 'app/shared/actions/UserManagementActions';
import PropertyStatus from 'app/shared/components/soh/PropertyStatus';
import BillingFilter from 'app/shared/components/Billing/BillingFilter';

const columnConfig = [
    {
        width: 5,
        dataKey: 'street',
        header: 'Property',
        isSortable: true,
        renderer: property => (
            <div>
                <Text as="p" fontColor="textDark">
                    {property.street}
                    {property.unit && ` - ${property.unit}`}
                </Text>
                {property.name && (
                    <Text as="p" fontColor="gray500">
                        {property.name}
                    </Text>
                )}
            </div>
        ),
    },
    {
        width: 2,
        dataKey: 'active',
        header: 'Status',
        isSortable: true,
        renderer: property => (
            <PropertyStatus
                code={property.active ? 'active' : 'inactive'}
                message={property.active ? 'Active' : 'Off - Market'}
            />
        ),
    },
    {
        width: 4,
        dataKey: 'city',
        header: 'City',
        isSortable: true,
        renderer: property => property.city,
    },
    {
        width: 2,
        dataKey: 'state',
        header: 'State',
        isSortable: true,
        renderer: property => property.state,
    },
];

const propTypes = {
    currentUser: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
};

const PropertyPicker = ({
    dispatch,
    onNextStep,
    onCancel,
    onBack,
    currentUser,
    isInvitation,
}) => {
    const createFilters = () => {
        const initialFilters = {};
        initialFilters.active = {
            title: 'Active Status',
            value: [
                {
                    label: 'Active',
                    isChecked: false,
                },
                {
                    label: 'Off - Market',
                    isChecked: false,
                },
            ],
            defaultValue: null,
            type: 'MULTISELECT',
        };
        return initialFilters;
    };
    const [filters, setFilters] = React.useState(createFilters());
    const [totalSelectedListings, setTotalSelectedListings] = React.useState(0);

    const getSelectedIndexes = (tableData) => {
        const indexes = new Set();
        const propertySet = new Set(currentUser.properties.map(property => property.listingId));
        tableData.forEach((property, i) => {
            if (propertySet.has(property.listingId)) {
                indexes.add(i);
            }
        });
        return indexes;
    };

    React.useEffect(() => {
        if (isInvitation) {
            dispatch(UserManagementActions.getInitialUserListings());
        }
    }, []);

    const handleUpdateProperties = (propertyIndexes, tableData) => {
        const newProperties = [];
        const availablePropertyMap = {};
        currentUser.availableProperties.forEach((property) => {
            availablePropertyMap[property.listingId] = property;
        });
        for (const index of propertyIndexes) {
            const { listingId } = tableData[index];
            newProperties.push(availablePropertyMap[listingId]);
        }
        dispatch(UserManagementActions.currentUserSetProperties(newProperties));
        setTotalSelectedListings(newProperties.length);
    };

    const renderTableHeader = () => (
        <div className="PropertyPicker-top-banner">
            <Text as="div" fontType="h6">
                {totalSelectedListings
                    ? `${totalSelectedListings} ${totalSelectedListings > 1 ? 'properties' : 'property'}`
                    : 'Select properties'}
            </Text>
        </div>
    );

    const isLoading = currentUser.propertiesLoadingState !== LoadingConstants.SUCCESS;

    React.useEffect(() => {
        if (currentUser.propertiesLoadingState === LoadingConstants.SUCCESS) {
            const newFilter = { ...filters };
            const states = new Set();
            const cities = new Set();

            currentUser.availableProperties.forEach((property) => {
                states.add(property.state);
            });
            currentUser.availableProperties.forEach((property) => {
                cities.add(property.city);
            });
            newFilter.cities = {
                title: 'City',
                value: Array.from(cities).map(city => ({
                    label: city,
                    isChecked: false,
                })),
                defaultValue: null,
                type: 'MULTISELECT',
            };
            newFilter.states = {
                title: 'State',
                value: Array.from(states).map(state => ({
                    label: state,
                    isChecked: false,
                })),
                defaultValue: null,
                type: 'MULTISELECT',
            };
            setFilters(newFilter);
        }
    }, [currentUser.propertiesLoadingState]);

    let filteredData = currentUser.availableProperties;
    if (currentUser.propertiesLoadingState === LoadingConstants.SUCCESS) {
        const activeSelected = filters.active.value[0].isChecked;
        const inactiveSelected = filters.active.value[1].isChecked;

        if (activeSelected || inactiveSelected) {
            filteredData = filteredData.filter(property => (property.active === activeSelected)
                || (!property.active === inactiveSelected));
        }

        if (filters.states) {
            const selectedStates = new Set(filters.states.value.filter(filter => filter.isChecked)
                .map(state => state.label));
            if (selectedStates.size) {
                filteredData = filteredData.filter(property => selectedStates.has(property.state));
            }
        }

        if (filters.cities) {
            const selectedCities = new Set(filters.cities.value.filter(filter => filter.isChecked)
                .map(city => city.label));
            if (selectedCities.size) {
                filteredData = filteredData.filter(property => selectedCities.has(property.city));
            }
        }
    }

    return (
        <div className="PropertyPicker">
            <div className="PropertyPicker-container">
                {isLoading ? <SpinnerContainer /> : (
                    <TableMFH
                        filters={filters}
                        onSaveFilters={setFilters}
                        defaultSortKey={{
                            key: 'street',
                            ascending: true,
                        }}
                        columnConfig={columnConfig}
                        data={filteredData}
                        idKey="zrnNumber"
                        selected={getSelectedIndexes}
                        onSelectRow={handleUpdateProperties}
                        TopBannerComponent={renderTableHeader}
                        rowSelectionDisabled={rowData => rowData.contactEmail === currentUser.email}
                        FilterComponent={BillingFilter}
                    />
                )}
            </div>
            <div className="Questions-footer">
                <TextButton className="Questions-button" onClick={onCancel}>
                    Cancel
                </TextButton>
                <div>
                    <TextButton className="Questions-button" onClick={onBack}>
                        Back
                    </TextButton>
                    <Button buttonType="primary" onClick={onNextStep} disabled={!currentUser.properties.length}>
                        Continue
                    </Button>
                </div>
            </div>
        </div>
    );
};


PropertyPicker.propTypes = propTypes;

const mapStateToProps = state => ({
    currentUser: UserManagementSelectors.makeCurrentUser(state),
});

export default connect(mapStateToProps)(PropertyPicker);
